import { Util, type Vec2 } from "miglib";
import type { GameObj } from "../GameObj";
import type { World } from "../World";
import type { TileType } from "../Tile";
import type { Faction } from "../types";
import { positionsInRadius } from "../geometry";
import type { UnitContext, PlaceContext, ObjContext, RenderContext, MakeContext, DefeatContext } from "./context";
import { InfluenceMap } from "../InfluenceMap";

export abstract class UnitType {

    abstract readonly id: string;
    abstract readonly displayName: string;

    abstract description(ctx: UnitContext): string;
    abstract make(ctx: MakeContext): GameObj;
    abstract maxCooldown(): number;

    name(ctx: UnitContext): string { return this.displayName; }
    img(ctx: UnitContext): string { return this.id; }
    playerCanDeploy(ctx: UnitContext): boolean { return false; }
    playerCanControl(ctx: ObjContext): boolean { return ctx.obj.faction === "player"; }
    canEnter(ctx: PlaceContext): boolean { return false; }
    cost(): { blood: number, souls: number, runes: number } { return { blood: 0, souls: 0, runes: 0 }; }
    defeats(): (string | true)[] { return []; }
    onDefeat(ctx: DefeatContext): void { }
    shouldTint(): boolean { return false; }
    step(ctx: ObjContext): void { }
    aiAlwaysSteps(): boolean { return true; }
    influenceRange(): number { return 0; }

    moveRange(): number { return 0; }
    canMove(): boolean { return this.moveRange() > 0; }

    getReachablePositions(x: number, y: number, isValid: (x: number, y: number) => boolean): Vec2[] {
        const range = this.moveRange();
        if (range === 0) return [];
        const positions = positionsInRadius(x, y, range);
        return positions.filter(({ x, y }) => isValid(x, y))
    }

    canDefeat(targetUnitId: string): boolean {
        return targetUnitId !== "cauldron" && targetUnitId !== "machine"
            && (this.defeats().includes(targetUnitId) || this.defeats().includes(true));
    }

    canHuntStaticObjectiveNear({ faction, world, game }: ObjContext, x: number, y: number): boolean {
        const obj = world.get(x, y);
        if (!obj) return false;
        const infl = game.influenceMap.get(x, y)
        if (infl === faction) return false;
        return obj.unitId === "machine" || obj.unitId === "cauldron";
    }

    inTile(ctx: PlaceContext, tileTypes: TileType[], world?: World): boolean {
        return (world === undefined || ctx.world === world) && tileTypes.includes(ctx.tile.type);
    }

    spreadInfluence(ctx: ObjContext, range: number, probability: number = 0.1): void {
        if (Math.random() >= probability) return;
        const { x, y, game, faction } = ctx;
        const candidates = positionsInRadius(x, y, range).filter(pos => {
            const current = game.influenceMap.get(pos.x, pos.y);
            return faction ? current !== faction : current !== undefined;
        });
        const place = candidates[Math.floor(Math.random() * candidates.length)];
        if (!place) return;
        if (faction === "neutral") game.influenceMap.remove(place.x, place.y);
        else if (faction) game.influenceMap.set(place.x, place.y, faction);
        else game.influenceMap.remove(place.x, place.y);
    }

    wander(ctx: ObjContext, range: number = 1): void {
        const { game, obj, world } = ctx;
        if (!obj || !obj.isReady(this.maxCooldown())) return;
        const nextPos = game.findRandomPosForObj(range, ctx, c => this.canEnter(c));
        if (nextPos != null) game.tryMove(obj, nextPos.x, nextPos.y, world);
        obj.setNotReady();
    }

    hunt(
        ctx: ObjContext,
        getPriority: (ctx: PlaceContext) => number,
    ): void {
        const { game, obj, unit, world } = ctx;
        if (!obj || !obj.isReady(this.maxCooldown())) return;
        const maxRadius = unit.moveRange() * 10;
        const range = Math.max(1, unit.moveRange() / 3);

        const target = this.nearestObjective(ctx, getPriority, maxRadius);

        let dest: { x: number, y: number } | null;
        if (target) {
            const dx = target.x - ctx.x;
            const dy = target.y - ctx.y;
            const canReach = dx * dx + dy * dy <= range * range;
            dest = canReach ? target : this.stepToward(ctx, target, range);
        } else {
            dest = game.findRandomPosForObj(range, ctx, c => this.canEnter(c));
        }

        if (dest) game.tryMove(obj, dest.x, dest.y, world);
        obj.setNotReady();
    }

    nearestObjective(ctx: ObjContext, getPriority: (ctx: PlaceContext) => number, maxRadius: number): { x: number, y: number } | null {
        const { world } = ctx;
        const dist2 = (p: { x: number, y: number }) => (p.x - ctx.x) ** 2 + (p.y - ctx.y) ** 2;
        const positions = positionsInRadius(ctx.x, ctx.y, maxRadius).sort((a, b) => dist2(a) - dist2(b));

        let best: { x: number, y: number } | null = null;
        let bestDist = 0;
        let bestPrio = 0;
        for (const pos of positions) {
            if (pos.x === ctx.x && pos.y === ctx.y) continue;
            const dist = dist2(pos);
            if (best && dist > bestDist) break;
            const tile = world.getTile(pos.x, pos.y);
            if (!tile) continue;
            const c: PlaceContext = { ...ctx, x: pos.x, y: pos.y, tile };
            if (!this.canEnter(c)) continue;
            const prio = getPriority(c);
            if (prio > 0 && (best === null || prio > bestPrio)) {
                best = { x: pos.x, y: pos.y };
                bestDist = dist;
                bestPrio = prio;
            }
        }
        return best;
    }

    stepToward(ctx: ObjContext, target: { x: number, y: number }, range: number): { x: number, y: number } | null {
        const { world } = ctx;
        let bestDist = Infinity;
        const ties: { x: number, y: number }[] = [];
        for (const pos of positionsInRadius(ctx.x, ctx.y, range)) {
            if (pos.x === ctx.x && pos.y === ctx.y) continue;
            const tile = world.getTile(pos.x, pos.y);
            if (!tile) continue;
            if (!this.canEnter({ ...ctx, x: pos.x, y: pos.y, tile })) continue;
            const dist = (pos.x - target.x) ** 2 + (pos.y - target.y) ** 2;
            if (dist < bestDist) { bestDist = dist; ties.length = 0; ties.push(pos); }
            else if (dist === bestDist) ties.push(pos);
        }
        return ties.length ? ties[Math.floor(Math.random() * ties.length)] : null;
    }

    render(ctx: RenderContext): void {
        const { g, isHovered, bounds, obj } = ctx;

        if (isHovered) {
            ctx.game.setHovered(obj);
            ctx.game.tryBeginObjDrag(ctx);
        }
        if (!obj.isReady(this.maxCooldown()) && obj.faction === "player" && this.canMove()) {
            g.sketch.tint(255, 128);
            g.renderImage(this.img(ctx), bounds.x, bounds.y);
            g.sketch.tint(255, 255);
        } else {
            g.renderImage(this.img(ctx), bounds.x, bounds.y);
        }
        if (isHovered || ctx.game.isDragObj(obj)) g.renderImage("select", bounds.x - 1, bounds.y - 1);
    }
}
